/**
 * Utilities for guest mode functionality
 * Handles guest progress and account conversion
 */

import { supabase } from '../supabase';

/**
 * Check if current user is a guest
 */
export const isGuestUser = (): boolean => {
  return localStorage.getItem('isGuest') === 'true';
};

/**
 * Get guest progress from localStorage
 */
export const getGuestProgress = () => {
  const progressKey = 'userProgress';
  const quizProgressKey = 'quizProgress';
  
  const progress = localStorage.getItem(progressKey);
  const quizProgress = localStorage.getItem(quizProgressKey);
  
  return {
    progress: progress ? JSON.parse(progress) : null,
    quizProgress: quizProgress ? JSON.parse(quizProgress) : null,
    hasProgress: !!(progress || quizProgress)
  };
};

/**
 * Merge guest progress to user account after registration/login
 */
export const mergeGuestProgressToAccount = async (userId: string): Promise<boolean> => {
  try {
    const { progress, hasProgress } = getGuestProgress();
    
    if (!hasProgress) {
      console.log('No guest progress to merge');
      return true;
    }

    console.log('Merging guest progress to account:', userId);

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No user found for merging progress');
      return false;
    }

    // 1. Merge user stats (XP, streak)
    if (progress) {
      const { error: userError } = await supabase
        .from('users')
        .upsert({
          id: userId,
          email: user.email,
          display_name: user.user_metadata?.full_name || user.email?.split('@')[0],
          streak: progress.streak || 0,
          xp: progress.totalXP || 0,
          last_login: new Date().toISOString(),
          level_recommended: 'A1'
        }, {
          onConflict: 'id'
        });

      if (userError) {
        console.error('Error merging user stats:', userError);
      } else {
        console.log('✅ Guest progress merged to database');
      }
    }

    // 2. Note: Quiz attempts are saved directly when completed, so we don't need to merge old ones
    // This prevents timestamp issues

    // 3. Clear guest flag after successful merge
    localStorage.removeItem('isGuest');
    
    // Keep localStorage progress for now (it will sync naturally)
    // Don't clear it - let it sync to database over time

    return true;
  } catch (error) {
    console.error('Error merging guest progress:', error);
    return false;
  }
};

/**
 * Check if guest has made significant progress (to prompt account creation)
 */
export const shouldPromptGuestToCreateAccount = (): boolean => {
  const { progress, hasProgress } = getGuestProgress();
  
  if (!hasProgress || !progress) {
    return false;
  }

  // Prompt if guest has made meaningful progress:
  // - Completed at least 2 lessons, OR
  // - Earned at least 100 XP, OR
  // - Has a streak of at least 2 days
  // This ensures we only prompt when user has invested time
  const hasCompletedLessons = progress.completedLessons && progress.completedLessons.length >= 2;
  const hasEarnedXP = progress.totalXP && progress.totalXP >= 100;
  const hasStreak = progress.streak && progress.streak >= 2;

  return hasCompletedLessons || hasEarnedXP || hasStreak;
};

